// =============================================================================
// VulnTracker — Main Layout with Sidebar
// =============================================================================
import { Fragment, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ShieldExclamationIcon,
  CubeIcon,
  BellIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  FolderIcon,
  CodeBracketIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { User as UserIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
  { name: 'Components', href: '/components', icon: CubeIcon },
  { name: 'Vulnerabilities', href: '/vulnerabilities', icon: ShieldExclamationIcon },
  { name: 'Notifications', href: '/notifications', icon: BellIcon },
  { name: 'Developer', href: '/developer', icon: CodeBracketIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  return (
    <div className="min-h-screen">
      {/* Mobile sidebar overlay */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                    <XMarkIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                <SidebarContent currentPath={location.pathname} user={user} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent currentPath={location.pathname} user={user} />
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            {/* User profile — top right */}
            <div className="flex items-center gap-x-3">
              {/* Name + role stacked — left side */}
              <div className="hidden sm:flex flex-col leading-tight text-right">
                <span className="text-sm font-semibold text-gray-900 truncate max-w-[160px]">
                  {user?.full_name || user?.email || 'User'}
                </span>
                <span className="text-xs text-gray-500 capitalize">
                  {user?.role?.replace('_', ' ') || ''}
                </span>
              </div>
              {/* User icon avatar — right side */}
              <div className="h-9 w-9 rounded-full bg-[#0EA5E9] flex items-center justify-center shrink-0">
                <UserIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────
interface SidebarProps {
  currentPath: string;
  user: { full_name: string; email: string; role: string } | null;
}

function SidebarContent({ currentPath, user }: SidebarProps) {
  return (
    <>
      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4">
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center">
          <ShieldExclamationIcon className="h-8 w-8 text-primary-400" />
          <span className="ml-2 text-xl font-bold text-white">VulnIntelligence</span>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col">
          <ul className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul className="-mx-2 space-y-1">
                {navigation.map((item) => {
                  const isActive =
                    item.href === '/'
                      ? currentPath === '/'
                      : currentPath.startsWith(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={clsx(
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                          isActive
                            ? 'bg-[#0EA5E9] text-white'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800',
                        )}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>

            {/* Bottom section */}
            <li className="mt-auto space-y-1">
              {/* About link */}
              <Link
                to="/about"
                className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                  currentPath === '/about'
                    ? 'bg-[#0EA5E9] text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                } transition-colors`}
              >
                <InformationCircleIcon className="h-6 w-6 shrink-0" />
                About
              </Link>

              {/* User info */}
              <div className="flex items-center gap-x-3 rounded-md p-2 text-sm text-gray-400">
                {/* User icon avatar — left */}
                <div className="h-9 w-9 rounded-full bg-[#0EA5E9] flex items-center justify-center shrink-0">
                  <UserIcon className="h-5 w-5 text-white" />
                </div>
                {/* Name + email stacked — right */}
                <div className="flex-1 truncate">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email || ''}
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </nav>
      </div>

    </>
  );
}
